import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { FaSearch } from 'react-icons/fa';
import { db } from '../../firebase.config';
import { formatBlogDate } from '../../utils/formatBlogDate';
import { sanitizeBlogHtml } from '../../utils/sanitizeBlogHtml';
import {
  fetchPublishedInsightBySegment,
  insightArticleHrefInContext,
  persistSlugForPublishedPostIfMissing,
  type InsightBlogPost,
} from '../../utils/insightArticle';
import '../../styles/kyro-design.css';
import '../../styles/blogRichText.css';
import './Insights.css';

const SITE_TITLE = 'Kyro Capital';

const Insights: React.FC = () => {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isArticle = Boolean(slugParam);

  const [posts, setPosts] = useState<InsightBlogPost[]>([]);
  const [loading, setLoading] = useState(!isArticle);
  const [searchQuery, setSearchQuery] = useState('');

  const [article, setArticle] = useState<InsightBlogPost | null>(null);
  const [articleLoading, setArticleLoading] = useState(isArticle);
  const [articleMissing, setArticleMissing] = useState(false);

  /* List: published posts by date */
  useEffect(() => {
    if (isArticle) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const blogQuery = query(
          collection(db, 'blogPosts'),
          where('published', '==', true),
          orderBy('date', 'desc')
        );
        const snap = await getDocs(blogQuery);
        if (cancelled) return;
        let rows: InsightBlogPost[] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as InsightBlogPost));
        try {
          for (const d of snap.docs) {
            const p = { id: d.id, ...d.data() } as InsightBlogPost;
            const u = await persistSlugForPublishedPostIfMissing(p);
            if (u.slug && u.slug !== p.slug) {
              rows = rows.map((r) => (r.id === d.id ? u : r));
            }
          }
        } catch (err) {
          console.warn('Insights slug backfill', err);
        }
        if (!cancelled) setPosts(rows);
      } catch (e) {
        console.error('Error loading insights:', e);
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isArticle]);

  /* Article: single post by slug or legacy id */
  useEffect(() => {
    if (!isArticle || !slugParam) return;
    let cancelled = false;
    (async () => {
      setArticleLoading(true);
      setArticleMissing(false);
      setArticle(null);
      try {
        const post = await fetchPublishedInsightBySegment(slugParam);
        if (cancelled) return;
        if (!post) {
          setArticleMissing(true);
          setArticle(null);
        } else {
          setArticle(post);
          setArticleMissing(false);
        }
      } catch (e) {
        console.error('Error loading article:', e);
        if (!cancelled) {
          setArticleMissing(true);
          setArticle(null);
        }
      } finally {
        if (!cancelled) setArticleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isArticle, slugParam]);

  /* Canonical URL: only upgrade /insights/:firestoreId → /insights/:slug when slug is globally unique.
   * If several posts share one slug, replacing would send everyone to the same URL and wrong article. */
  useEffect(() => {
    if (!article || !slugParam) return;
    const decoded = decodeURIComponent(slugParam);
    const slug = typeof article.slug === 'string' ? article.slug.trim() : '';
    if (!slug || decoded === slug) return;
    if (decoded !== article.id) return;

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'blogPosts'), where('slug', '==', slug), limit(2))
        );
        if (cancelled) return;
        if (snap.docs.length !== 1 || snap.docs[0].id !== article.id) return;
        navigate(`/insights/${encodeURIComponent(slug)}`, { replace: true });
      } catch (e) {
        console.warn('Insights canonical URL check', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [article, slugParam, navigate]);

  useEffect(() => {
    if (!article) return;
    const prev = document.title;
    document.title = `${article.title} | ${SITE_TITLE}`;
    return () => {
      document.title = prev;
    };
  }, [article]);

  const filtered = posts.filter(
    (p) =>
      !searchQuery.trim() ||
      p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isArticle) {
    return (
      <div className="page insights-page insights-page--article">
        <div className="insights-article-shell">
          <div className="insights-inner insights-article-inner">
            <Link to="/insights" className="insights-back">
              ← All insights
            </Link>

            {articleLoading ? (
              <p className="insights-article-loading">Loading…</p>
            ) : articleMissing || !article ? (
              <div className="insights-article-card insights-article-card--plain">
                <div className="insights-article-notfound">
                  <h1 className="insights-article-notfound-title">Article not found</h1>
                  <p>This link may be incorrect or the article may no longer be published.</p>
                  <Link to="/insights" className="insights-read-more">
                    Back to all insights
                  </Link>
                </div>
              </div>
            ) : (
              <article className="insights-article-card">
                <div className="insights-article-eyebrow" aria-hidden>
                  <span className="insights-article-eyebrow-dot" />
                  <span className="insights-article-eyebrow-line" />
                  Insights
                </div>
                <h1 className="insights-article-title">{article.title}</h1>
                <p className="insights-date insights-article-meta">{formatBlogDate(article.date)}</p>
                {article.imageURL ? (
                  <img
                    src={article.imageURL}
                    alt=""
                    className="insights-article-image"
                    loading="eager"
                  />
                ) : null}
                <div
                  className="insights-article-body blog-rich-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(article.content) }}
                />
              </article>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page insights-page">
      <div className="hero" style={{ minHeight: '42vh', paddingBottom: '3rem' }}>
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner">
          <div className="kicker">
            <div className="kicker-dot" />
            <div className="kicker-line" />
            Insights
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            Perspectives &amp; updates
            <br />
            <span className="dim">From the Kyro team.</span>
          </h1>
          <p className="hero-sub" style={{ maxWidth: 560 }}>
            Articles and commentary for investors and partners. For confidential reports and documents, use the{' '}
            <a href="/investor-login" style={{ color: 'var(--blue)', fontWeight: 500 }}>
              investor portal
            </a>
            .
          </p>
        </div>
      </div>

      <div className="sec">
        <div className="insights-inner">
          {loading ? (
            <p className="insights-empty" style={{ paddingTop: '2rem' }}>
              Loading…
            </p>
          ) : (
            <>
              <div className="insights-search">
                <FaSearch size={16} aria-hidden />
                <input
                  type="search"
                  placeholder="Search articles…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search articles"
                />
              </div>

              <div className="insights-blog-list">
                {filtered.map((post) => (
                  <article key={post.id} className="insights-blog-item">
                    <Link
                      to={insightArticleHrefInContext(post, posts)}
                      className="insights-blog-card-hit"
                      aria-labelledby={`insights-card-title-${post.id}`}
                    >
                      {post.imageURL ? (
                        <img
                          src={post.imageURL}
                          alt=""
                          loading="lazy"
                          className="insights-blog-card-image"
                        />
                      ) : null}
                      <h2 id={`insights-card-title-${post.id}`}>{post.title}</h2>
                      <p className="insights-date">{formatBlogDate(post.date)}</p>
                      <p className="insights-excerpt">{post.excerpt}</p>
                      <span className="insights-read-more">Read article</span>
                    </Link>
                  </article>
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="insights-empty">
                  <p>
                    {posts.length === 0
                      ? 'No published articles yet. Check back soon.'
                      : 'No articles match your search.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Insights;
